'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import WaveDevider from '@publicImages/sprites/wave-devider.svg';

import AccessKeyBanner from '@/components/AccessKeyBanner';
import CelebrationBackSearchButton from '@/components/BackSearchButton/CelebrationBackSearchButton';
import BackToTopButton from '@/components/BackToTopButton';
import CabinPrice from '@/components/CabinCard/CabinPrice';
import FullCruisePortsOfCall from '@/components/ChooseVoyage/FullCruisePortsOfCall';
import CabinCards from '@/components/ChooseVoyage/ItineraryFeatureCards';
import ImageVideo from '@/components/ChooseVoyage/PackageCardDetails/ImageVideo/ImageVideo';
import { ViewMoreDates, ViewMoreDatesFlyout } from '@/components/ChooseVoyage/ViewMoreDates';
import VoyageExperience from '@/components/ChooseVoyage/VoyageExperience';
import WhatsIncluded from '@/components/ChooseVoyage/WhatsIncluded';
import Button from '@/components/elements/Button';
import Location from '@/components/Icon/Location';
import Ship from '@/components/Icon/Ship';
import Image from '@/components/Image';
import PromoBannerPlace from '@/components/PromoBanner/Place';
import { getCelebrationOnBackHandler } from '@/components/Promotion/CelebrationBanner/helpers';
import ShareButton from '@/components/ShareButton';
import UIResource from '@/components/UIResource';
import Uplift from '@/components/Uplift';
import WithFrenchContent from '@/components/WithFrenchContent';
import { selectPriceType, selectSailors } from '@/ducks/filters/selectors';
import { selectMainPackagesIsLoaded } from '@/ducks/pages/chooseVoyage/selectors';
import { selectCelebrationsIsFlowActive } from '@/ducks/programs/celebrations/selectors';
import {
  selectIsSailingDataLoaded,
  selectSailingData,
  selectSelectedPackageFromMainPackages,
} from '@/ducks/voyageInfo/sailing/selectors';
import BarTabBadge from '@/features/promo/FullDetailsBarTab';
import { PromoBannerPlaceId } from '@/features/promoBanners/types';
import withSideqikScriptForBookingFlow from '@/helpers/hoc/utility/withSideqikScriptForBookingFlow';
import { checkForValueType, decodeInnerHTML, getShipNames } from '@/helpers/util/misc';
import { SafariNavbarOverlapToggle } from '@/hooks/useSafariNavbarOverlapToggle';
import { FiltersPriceType } from '@/infra/types/common/filters';
import ChatbotPositioningElement from '@/layouts/Wrappers/Chatbot/ChatbotPositioningElement';
import { selectCabinCategories } from '@/reducers/voyagePlanner/chooseCabin/cabinCategories/selectors';
import { useAppSelector } from '@/store';
import tagmanager from '@/tagmanager';
import { TrackAppearance, trackOnScrollAction } from '@/tagmanager/containers/TrackAppearance';

import Skeleton from './Skeleton';

import './FullCruiseDetails.scss';

const {
  tracker: {
    voyageDetails: { initTracker, SOCIAL_SHARE_POSITIONS, trackOnPortClick, trackOnShareClick },
  },
} = tagmanager;

const itineraryImageSkeletonProps = { height: '384.5px', width: '100%' };

const VIEW_MORE_DATES_TEXT_CMS_ID = 'ChooseVoyage.ViewDates.CTA';

const onItineraryItemClick = (itinerary) => {
  trackOnPortClick(itinerary?.portName);
};

const onShareClick = (position) => (platform) => {
  trackOnShareClick(platform, position);
};

const FullCruiseDetails = () => {
  const sailingData = useAppSelector(selectSailingData);
  const sailorCount = useAppSelector(selectSailors);
  const priceType = useAppSelector(selectPriceType);
  const cabinCategories = useAppSelector(selectCabinCategories);
  const isCelebrationFlowActive = useAppSelector(selectCelebrationsIsFlowActive);

  const searchParams = useSearchParams();
  const isReady = useAppSelector(
    (state) => selectMainPackagesIsLoaded(state) && selectIsSailingDataLoaded(state, searchParams),
  );
  const selectedPackage = useAppSelector((state) => selectSelectedPackageFromMainPackages(state, searchParams));

  useEffect(() => {
    initTracker(selectedPackage);
  }, [selectedPackage]);

  useEffect(() => {
    // This attribute is needed for CMS to adjust header styling according to book pod expectations
    const disableMyAccountMargin = 'data-disable-my-account-margin';
    document.body.setAttribute(disableMyAccountMargin, 'true');
    return () => {
      document.body.removeAttribute(disableMyAccountMargin);
    };
  }, []);

  if (!sailingData) {
    return null;
  }

  const { duration = 5, packageName = '' } = sailingData;
  const tripType = selectedPackage?.tripType || '';
  const { activitiesHeading, activitiesImage, activitiesIntro, image, sizzleReel } = sailingData;

  const amount = sailingData?.startingPrice?.amount;
  const shipNames = getShipNames(selectedPackage);
  const sailingCount = selectedPackage?.sailingList?.length;

  const trackingArgs = {
    data: { cabinCategories, sailing: sailingData },
    key: tagmanager.observer.TRACKING_EVENTS_FOR_OBSERVER.CHOOSE_CABIN_CATEGORY_APPEARED,
  };

  if (!isReady) {
    return (
      <div className="FullCruiseDetailsPage">
        <Skeleton backHandler={getCelebrationOnBackHandler(isCelebrationFlowActive)} />
        <BackToTopButton />
      </div>
    );
  }

  return (
    <div className="FullCruiseDetailsPage">
      <div className="ItineraryDetailsMain">
        <PromoBannerPlace placeId={PromoBannerPlaceId.fullCruiseDetailsA} />
        <AccessKeyBanner className="akBanner" />
        <section className="searchShare ItineraryDetails-container">
          <CelebrationBackSearchButton onClick={getCelebrationOnBackHandler(isCelebrationFlowActive)} />
          <div className="mobileShareIcon">
            <ShareButton
              isShortLink
              link={sailingData?.bookingShareLink}
              modalAlign="end"
              modalSide="bottom"
              onShare={onShareClick(SOCIAL_SHARE_POSITIONS.TOP)}
              sailingData={sailingData}
            />
          </div>
        </section>

        <div className="ItineraryVideoMobile">
          <ImageVideo
            fill={true}
            hasSizzleReelsControls
            imgSrc={image?.src}
            skeletonProps={itineraryImageSkeletonProps}
            videoSrc={sizzleReel?.src}
          />
          <BarTabBadge duration={selectedPackage?.duration} sailingList={selectedPackage?.sailingList} />
        </div>

        <div className="ItineraryDetails ItineraryDetails-container">
          <div className="ItineraryDetails__title" id="datesOverlayPackageTitle">
            <WithFrenchContent node="h1" text={packageName} />
          </div>
          <div className="description">
            <div className="nightsShipName">
              <span className="shipIcon">
                <Ship />
              </span>
              <span className="nightsShipNameText">
                <UIResource id="itineraryOverlay.nightsShipName" values={{ N: duration, shipName: shipNames }} />
              </span>
            </div>
            <div className="tripTypePort">
              <span className="locationIcon">
                <Location />
              </span>
              <span className="tripTypePortText">
                <UIResource
                  id="itineraryOverlay.tripTypePort.text"
                  values={{
                    country: sailingData?.ports?.[0]?.countryName || '',
                    depaturePort: sailingData?.ports?.[0]?.portName || '',
                    tripType,
                  }}
                />
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="FullCruiseDetailsMain">
        <div className="FullCruiseDetailsLeft">
          <div className="ItineraryImageMoreDatesSection">
            <div className="ItineraryVideo">
              <ImageVideo
                fill={true}
                hasSizzleReelsControls
                imgSrc={image?.src}
                skeletonProps={itineraryImageSkeletonProps}
                videoSrc={sizzleReel?.src}
              />
              <BarTabBadge duration={selectedPackage?.duration} sailingList={selectedPackage?.sailingList} />
            </div>
          </div>

          <FullCruisePortsOfCall onItemClick={onItineraryItemClick} tabDefaultValue="day-by-day" />
          <VoyageExperience />
          <div className="ActivitiesImageDescription">
            <Image
              alt={activitiesImage?.alt || ''}
              aria-hidden
              aspectRatio={1}
              className="ActivitiesImage"
              src={activitiesImage?.src || ''}
            />
            <div className="ActivitiesTextContent">
              <div className="ActivitiesHeading">{activitiesHeading}</div>
              <div className="ActivitiesDescription">{decodeInnerHTML(activitiesIntro)}</div>
            </div>
          </div>
        </div>
        <div className="FullCruiseDetailsRight">
          <ShareButton
            link={sailingData?.bookingShareLink}
            modalAlign="end"
            modalSide="top"
            onShare={onShareClick(SOCIAL_SHARE_POSITIONS.TOP)}
            sailingData={sailingData}
          />
          {selectedPackage && (
            <ViewMoreDates
              onItemClick={tagmanager.tracker.voyageDetails.trackNextSailingDateClick}
              packageData={selectedPackage}
            />
          )}
        </div>
      </div>
      <img alt="" className="wave-devider" src={WaveDevider} />
      <TrackAppearance
        className="cabinSuitesWhatsIncludedSection"
        dataToTrack={trackingArgs}
        intersectionThreshold={0.5}
        onAppearanceAction={trackOnScrollAction}
        oncePerPage
      >
        <CabinCards
          cabinCategoriesData={cabinCategories}
          onClick={tagmanager.tracker.voyageDetails.trackCabinTypeClick}
        />
      </TrackAppearance>
      <div className="cabinSuitesDivider" />
      <div className="FullCruiseDetailsMain">
        <WhatsIncluded />
      </div>
      <div className="FullCruiseDetailsMain">
        <div className="FullCruiseDetailsLeft">
          <div className="ActivitiesImageDescription mobile">
            <Image
              alt={activitiesImage?.alt || ''}
              aria-hidden
              aspectRatio={1}
              className="ActivitiesImage"
              src={activitiesImage?.src || ''}
            />
            <div className="ActivitiesTextContent">
              <div className="ActivitiesHeading">{activitiesHeading}</div>
              <div className="ActivitiesDescription">{decodeInnerHTML(activitiesIntro)}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="divider" />
      <div className="divider" />
      <div className="dreamShare">
        <h3 className="title">
          <UIResource id="Itinerary.Share.footerTitle" />
        </h3>
        <ShareButton
          isShortLink
          link={sailingData?.bookingShareLink}
          modalAlign="center"
          modalSide="top"
          onShare={onShareClick(SOCIAL_SHARE_POSITIONS.TOP)}
          sailingData={sailingData}
        />
      </div>
      <SafariNavbarOverlapToggle />
      <ChatbotPositioningElement className="viewMoreStickySection">
        <div className="sailingAvgPriceSection">
          <div className="sailingAvgPrice">
            <CabinPrice
              abTestPriceType={priceType}
              currencyCode={sailingData?.startingPrice?.currencyCode}
              duration={sailingData?.duration}
              isForceLineBreak={false}
              sailors={sailorCount}
              startingPrice={sailingData?.startingPrice}
            />
          </div>
          <div className="sailingPriceFootNote">
            <UIResource
              id="ViewDatesOverlay.priceDetail"
              values={{
                cabin: priceType === FiltersPriceType.perCabin,
                Sailor: priceType === FiltersPriceType.perSailor,
                'Sailor per night': priceType === FiltersPriceType.sailorPerNight,
              }}
            />
          </div>
          <div className="sailingUpliftPrice">
            {checkForValueType(sailingData?.ports) && checkForValueType(sailingData?.startDate) && (
              <Uplift
                currencyCode={sailingData?.startingPrice?.currencyCode}
                isHorizontal
                isTooltipDisabled={false}
                price={amount}
                sailing={sailingData}
                sailors={sailorCount}
                showOrFromLabel
              />
            )}
          </div>
        </div>
        <div className="viewMoreDatesStickyButton">
          {selectedPackage && (
            <ViewMoreDatesFlyout
              onItemClick={tagmanager.tracker.voyageDetails.trackNextSailingDateFlyoutClick}
              onOpenChange={tagmanager.tracker.voyageDetails.trackViewMoreDatesToggle}
              packageData={selectedPackage}
            >
              <Button variant="primary">
                <UIResource id={VIEW_MORE_DATES_TEXT_CMS_ID} values={{ N: sailingCount }} />
              </Button>
            </ViewMoreDatesFlyout>
          )}
        </div>
      </ChatbotPositioningElement>

      <BackToTopButton />
    </div>
  );
};

export default withSideqikScriptForBookingFlow(FullCruiseDetails, {});
